import React from "react";
import {
  Container,
  HeadingContainer,
  Heading,
  ProjectsContainer,
  ProjectContent,
  ProjectDescription,
  ProjectImage,
  ProjectImageLink,
  ProjectName,
  ProjectTitleLink,
  ProjectLabel,
  ProjectSkill,
  ProjectStack,
  Project,
  Text,
} from "./Projects.Styled";
import { motion } from "framer-motion";
import { HashLink } from "react-router-hash-link";
import { commonUpAppearVariants } from "../../animations/motion";

import e8Markets from "../../assets/e8markets.com-mockup-1200.webp";
import e8MarketsSmall from "../../assets/e8markets.com-mockup-600.webp";
import magicCraft from "../../assets/magiccraft.io-mockup-1200.webp";
import magicCraftSmall from "../../assets/magiccraft.io-mockup-600.webp";
import sensay from "../../assets/sensay.io-mockup-1200.webp";
import sensaySmall from "../../assets/sensay.io-mockup-600.webp";
import vStudio from "../../assets/v-studio.cc-mockup-1200.webp";
import vStudioSmall from "../../assets/v-studio.cc-mockup-600.webp";

const Websites = [
  {
    image: {
      url: e8Markets,
      smallUrl: e8MarketsSmall,
      alt: "E8 Markets website",
    },
    name: "E8 Markets",
    link: "https://e8markets.com/",
    experienceKey: "e8-markets",
    description:
      "Building and improving production features across E8 Markets’ Next.js and Nuxt products, with close attention to complex customer journeys.",
    stack: [
      "Next.js",
      "Nuxt",
      "TypeScript",
      "JavaScript",
      "Tailwind CSS",
      "Zustand",
      "Figma",
    ],
  },
  {
    image: {
      url: sensay,
      smallUrl: sensaySmall,
      alt: "Sensay website",
    },
    name: "Sensay",
    link: "https://sensay.io/",
    experienceKey: "sensay",
    description:
      "Shipped user-facing features and product fixes across Sensay’s AI platform, from responsive UI work to production issue resolution.",
    stack: [
      "Next.js",
      "TypeScript",
      "Tailwind CSS",
      "Supabase",
      "Zustand",
      "ElevenLabs",
      "Vercel",
    ],
  },
  {
    image: {
      url: magicCraft,
      smallUrl: magicCraftSmall,
      alt: "MagicCraft website",
    },
    name: "MagicCraft",
    link: "https://magiccraft.io/",
    experienceKey: "magiccraft",
    description:
      "Built and maintained front-end experiences for MagicCraft’s NFT Marketplace and Game Lobby using React, TypeScript, Ethers.js, and Redux.",
    stack: [
      "React",
      "TypeScript",
      "Ethers.js",
      "Redux",
      "Styled Components",
      "Sass",
      "REST APIs",
    ],
  },
  {
    image: {
      url: vStudio,
      smallUrl: vStudioSmall,
      alt: "V-Studio website",
    },
    name: "V-Studio",
    link: "https://v-studio.cc/",
    experienceKey: "v-studio",
    description:
      "Building V-Studio’s customer-facing platform, translating its service offering into responsive pages and a focused journey for requesting tailored web projects.",
    stack: ["React", "JavaScript", "CSS", "Figma", "GitHub"],
  },
];

const AnimatedProject = ({ index, web }) => {
  const isReversed = index % 2 !== 0;

  return (
    <Project
      $reversed={isReversed}
      as={motion.article}
      variants={commonUpAppearVariants}
      initial="closed"
      whileInView="open"
      viewport={{ once: true, amount: 0.18 }}
    >
      <ProjectImageLink
        href={web.link}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={`Visit ${web.name}`}
      >
        <ProjectImage
          src={web.image.url}
          srcSet={`${web.image.smallUrl} 600w, ${web.image.url} 1200w`}
          sizes="(max-width: 563px) calc(100vw - 40px), (max-width: 1023px) calc(100vw - 120px), (max-width: 1099px) calc(66vw - 158px), 568px"
          alt={web.image.alt}
          width="1200"
          height="709"
          loading="lazy"
          decoding="async"
        />
      </ProjectImageLink>
      <ProjectContent $reversed={isReversed}>
        <ProjectLabel>Selected experience</ProjectLabel>
        <ProjectName>
          <ProjectTitleLink
            href={web.link}
            target="_blank"
            rel="noreferrer noopener"
          >
            {web.name}
          </ProjectTitleLink>
        </ProjectName>
        <ProjectDescription>
          <Text>{web.description}</Text>
        </ProjectDescription>
        <ProjectStack $reversed={isReversed}>
          {web.stack.slice(0, 2).map((skill) => (
            <ProjectSkill key={skill}>{skill}</ProjectSkill>
          ))}
          <ProjectSkill as="li">
            <HashLink to={`/?job=${web.experienceKey}#Experience`}>
              View role →
            </HashLink>
          </ProjectSkill>
        </ProjectStack>
      </ProjectContent>
    </Project>
  );
};

const Projects = () => {
  return (
    <Container id="Work">
      <HeadingContainer>
        03. <Heading>Featured Product Work</Heading>
      </HeadingContainer>
      <ProjectsContainer>
        {Websites.map((web, i) => (
          <AnimatedProject key={web.name} web={web} index={i} />
        ))}
      </ProjectsContainer>
    </Container>
  );
};

export default Projects;
